import { HttpResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { AiKnowledgeService } from '@galaxy/ai-knowledge';
import { TranslateService } from '@ngx-translate/core';
import { toObservable } from '@angular/core/rxjs-interop';
import {
  AssistantApiService,
  AssistantInterface,
  AssistantType,
  ConnectionInterface,
  PromptModuleApiService,
  PromptModuleKey,
  UpsertAssistantResponse,
} from '@vendasta/ai-assistants';
import { ConversationApiService } from '@vendasta/conversation';
import { KnowledgeSource } from '@vendasta/embeddings';
import { formatPhoneNumber } from '@vendasta/galaxy/pipes/src/phone-number/phone-number.pipe';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import {
  catchError,
  combineLatest,
  filter,
  firstValueFrom,
  from,
  map,
  Observable,
  of,
  shareReplay,
  switchMap,
} from 'rxjs';
import { WEBCHAT_WIDGET_CONNECTION_TYPE } from '../../../core';
import { knowledgeAppType, SNAPSHOT_WIDGET_CONNECTION_TYPE } from '../../../core/ai-assistant.constants';
import { AiAssistantForm, AiKnowledgeFormArray } from '../../../core/forms';
import { AiAssistant, AiConnection, ConnectionType } from '../../../core/interfaces/ai-assistant.interface';
import { AiAssistantService } from '../../../core/services/ai-assistant.service';
import { AiToolFormRegistryService } from '../../../core/services/ai-tool-form-registry.service';
import { SmsDetailsService } from '../../../core/services/sms-details.service';
import { WhatsappDetailsService } from '../../../core/services/whatsapp-details.service';
import {
  ACCOUNT_GROUP_ID_TOKEN,
  NAMESPACE_CONFIG_TOKEN,
  PARTNER_ID_TOKEN,
  SHOW_CUSTOM_ASSISTANT_TOKEN,
} from '../../../core/tokens';

const COMMUNICATION_CONNECTIONS = [
  ConnectionType.SMS,
  ConnectionType.WhatsApp,
  ConnectionType.WebchatWidget,
  ConnectionType.Voice,
  ConnectionType.SnapshotWidget,
  ConnectionType.AutoReviewResponse,
  ConnectionType.Instagram,
  ConnectionType.Facebook,
];

@Injectable()
export class AiAssistantFormV2Service {
  private readonly partnerId$ = inject(PARTNER_ID_TOKEN);
  private readonly accountGroupId$ = inject(ACCOUNT_GROUP_ID_TOKEN);
  private readonly showCustomAssistant$ = inject(SHOW_CUSTOM_ASSISTANT_TOKEN);
  private readonly aiAssistantService = inject(AiAssistantService);
  private readonly assistantApiService = inject(AssistantApiService);
  private readonly promptModuleApiService = inject(PromptModuleApiService);
  private readonly route = inject(ActivatedRoute);
  private readonly snackbarService = inject(SnackbarService);
  private readonly conversationApiService = inject(ConversationApiService);
  private readonly knowledgeService = inject(AiKnowledgeService);
  private readonly toolFormRegistry = inject(AiToolFormRegistryService);
  private readonly namespaceConfig$ = inject(NAMESPACE_CONFIG_TOKEN);
  private readonly translateService = inject(TranslateService);
  private readonly smsDetailsService = inject(SmsDetailsService);
  private readonly whatsappDetailsService = inject(WhatsappDetailsService);

  private readonly assistantId$ = this.route.paramMap.pipe(
    map((params) => params.get('assistantId') || ''),
    shareReplay({ refCount: true, bufferSize: 1 }),
  );

  public readonly isChangeChannelsToTriggersEnabled$ = toObservable(
    this.aiAssistantService.isChangeChannelsToTriggersEnabled,
  );

  public readonly showAutomations$ = toObservable(this.aiAssistantService.automationsEnabledOnEmployeePage);

  readonly aiAssistant$ = combineLatest([
    this.assistantId$,
    this.partnerId$,
    this.accountGroupId$,
    this.showCustomAssistant$,
  ]).pipe(
    switchMap(([assistantId, partnerId, accountGroupId, showCustomAssistant]): Observable<AiAssistant | null> => {
      // Assistant should always exist here
      if (!assistantId) {
        this.snackbarService.openErrorSnack('AI_ASSISTANT.SETTINGS.ASSISTANT_NOT_FOUND');
        return of(null);
      }

      return this.aiAssistantService.getHydratedAssistant(partnerId, accountGroupId, assistantId).pipe(
        map((hydratedAssistant) => {
          if (hydratedAssistant?.assistant?.type === AssistantType.ASSISTANT_TYPE_CUSTOM && !showCustomAssistant) {
            return null;
          }
          return hydratedAssistant;
        }),
        catchError((err) => {
          if (err.status === 404) {
            this.snackbarService.openErrorSnack('AI_ASSISTANT.SETTINGS.ASSISTANT_NOT_FOUND');
          } else {
            this.snackbarService.openErrorSnack('AI_ASSISTANT.SETTINGS.ERROR_FETCHING_ASSISTANT');
          }
          return of(null);
        }),
      );
    }),
    shareReplay({ refCount: true, bufferSize: 1 }),
  );

  readonly promptContents$: Observable<Record<string, string>> = this.aiAssistant$.pipe(
    switchMap((assistant) => {
      if (!assistant) return of({});
      const namespace = assistant.assistant.namespace;
      if (namespace === undefined) {
        return of({});
      }

      const promptModuleKeys =
        assistant.assistant.configurableGoals
          ?.map(
            (goal) =>
              goal.goal?.promptModules?.map((pm) => new PromptModuleKey({ id: pm.id, namespace: pm.namespace })) || [],
          )
          .flat() || [];

      if (promptModuleKeys.length === 0) {
        return of({});
      }

      return from(this.aiAssistantService.getMultiPromptModuleVersions(promptModuleKeys));
    }),
  );

  readonly knowledge$ = this.assistantId$.pipe(switchMap((assistantId) => this.getKnowledge(assistantId)));

  readonly availableModels = this.aiAssistantService.listAvailableModels();

  private readonly aiConnectionsInfo$ = combineLatest([
    this.namespaceConfig$,
    this.aiAssistant$,
    this.isChangeChannelsToTriggersEnabled$,
  ]).pipe(
    switchMap(([namespaceConfig, aiAssistant, isChangeChannelsToTriggersEnabled]) => {
      if (!aiAssistant?.assistant?.id) {
        return of([]);
      }
      return this.aiAssistantService
        .listConnectionsForAssistant(namespaceConfig.namespace, aiAssistant?.assistant?.id)
        .pipe(
          catchError(() => {
            if (isChangeChannelsToTriggersEnabled) {
              this.snackbarService.openErrorSnack('AI_ASSISTANT.SETTINGS.TRIGGERS.FETCH_ERROR');
            } else {
              this.snackbarService.openErrorSnack('AI_ASSISTANT.SETTINGS.CHANNELS.FETCH_ERROR');
            }
            return of([] as AiConnection[]);
          }),
        );
    }),
    shareReplay({ refCount: true, bufferSize: 1 }),
  );

  private readonly widgetConnectionNames$ = combineLatest([
    this.aiConnectionsInfo$,
    this.isChangeChannelsToTriggersEnabled$,
  ]).pipe(
    switchMap(([aiConnections, isChangeChannelsToTriggersEnabled]) => {
      const webchatWidgetConnectionIds = aiConnections
        .filter(
          (aiConnection) =>
            aiConnection.connection.connectionType === WEBCHAT_WIDGET_CONNECTION_TYPE ||
            aiConnection.connection.connectionType === SNAPSHOT_WIDGET_CONNECTION_TYPE,
        )
        .map((aiConnection) => aiConnection.connection.id);

      const widgetIds = webchatWidgetConnectionIds.filter((id): id is string => !id || !id.includes('default'));
      if (widgetIds?.length > 0) {
        return this.conversationApiService.getMultiWidget({ widgetIds: widgetIds }).pipe(
          map((response) => response.widgets),
          catchError(() => {
            if (isChangeChannelsToTriggersEnabled) {
              this.snackbarService.openErrorSnack('AI_ASSISTANT.SETTINGS.TRIGGERS.FETCH_WIDGET_NAME_WARNING');
            } else {
              this.snackbarService.openErrorSnack('AI_ASSISTANT.SETTINGS.CHANNELS.FETCH_WIDGET_NAME_WARNING');
            }
            return of([]);
          }),
        );
      }
      return of([]);
    }),
    map((widgets) => {
      return widgets.reduce(
        (acc, widget) => {
          acc[widget.widgetId] = widget.name;
          return acc;
        },
        {} as Record<string, string>,
      );
    }),
  );

  public readonly aiConnections$ = combineLatest([
    this.aiConnectionsInfo$,
    this.widgetConnectionNames$,
    this.smsDetailsService.phoneNumber$,
    this.whatsappDetailsService.phoneNumber$,
    this.isChangeChannelsToTriggersEnabled$,
  ]).pipe(
    map(([aiConnections, widgetConnectionNames, smsNumber, whatsappPhoneNumber, isChangeChannelsToTriggersEnabled]) => {
      return aiConnections.map((aiConnection) => {
        const connection = aiConnection.connection;
        let phoneNumber;
        switch (connection.connectionType) {
          case ConnectionType.SMS:
            phoneNumber = smsNumber;
            break;
          case ConnectionType.WhatsApp:
            phoneNumber = whatsappPhoneNumber;
            break;
          case ConnectionType.Voice:
            phoneNumber = smsNumber;
            break;
        }

        connection.connectionType === ConnectionType.SMS ? smsNumber : '';
        const connectionName = this.getConnectionName(
          connection,
          widgetConnectionNames,
          phoneNumber,
          isChangeChannelsToTriggersEnabled,
        );
        return {
          ...aiConnection,
          connection: {
            ...connection,
            name: connectionName,
          },
        };
      });
    }),
    shareReplay({ refCount: true, bufferSize: 1 }),
  );

  private readonly voiceFeatures$ = this.aiAssistantService.voiceFeatures$;

  // Fetch existing template variables for the assistant
  public readonly assistantTemplateVariables$ = this.aiAssistant$.pipe(
    switchMap((aiAssistant) => {
      const assistantId = aiAssistant?.assistant?.id;
      if (assistantId && aiAssistant?.assistant?.namespace) {
        return this.assistantApiService
          .listTemplateVariables({
            assistantKey: {
              id: assistantId,
              namespace: aiAssistant.assistant.namespace,
            },
          })
          .pipe(
            map((response: any) => {
              const variables = response.assistantScopedVariables.map((v: any) => v.key);
              return variables;
            }),
            catchError((err) => {
              console.error('Error fetching template variables for assistant:', assistantId, err);
              return of([]);
            }),
          );
      }
      return of([]);
    }),
    map((variables) => {
      const variableSet = new Set(variables);
      return variableSet;
    }),
    shareReplay({ refCount: true, bufferSize: 1 }),
  );

  public readonly form$ = combineLatest([
    this.aiAssistant$,
    this.promptContents$,
    this.aiConnections$,
    this.voiceFeatures$,
    // TODO: Temporarily allowing the old form component to be responsible for fetching and setting knowledge sources
    // this.knowledge$,
    of([]),
  ]).pipe(
    map(([assistant, promptModuleContents, hydratedAiConnections, voiceFeatures, knowledgeSources]) => {
      if (!assistant) return null;
      assistant.connections = hydratedAiConnections;
      return new AiAssistantForm(
        this.promptModuleApiService,
        assistant,
        promptModuleContents,
        knowledgeSources,
        this.toolFormRegistry,
        voiceFeatures,
        this.translateService,
      );
    }),
    filter((form) => !!form),
    shareReplay({ refCount: true, bufferSize: 1 }),
  );

  public getKnowledge(assistantId: string): Observable<KnowledgeSource[]> {
    const businessKSID = this.knowledgeService.businessProfileKSId();
    return from(this.knowledgeService.listAllKnowledgeSourcesForApp('APP-AI' + assistantId)).pipe(
      map((knowledgeSources) => {
        const businessIndex = knowledgeSources.findIndex((ks) => ks.id === businessKSID);

        if (businessIndex > -1) {
          const businessKS = knowledgeSources[businessIndex];
          return [
            businessKS,
            ...knowledgeSources.slice(0, businessIndex),
            ...knowledgeSources.slice(businessIndex + 1),
          ];
        }
        return knowledgeSources;
      }),
    );
  }

  public upsertAssistant(assistant: AssistantInterface, applyDefaults = false): Observable<UpsertAssistantResponse> {
    return this.assistantApiService.upsertAssistant({
      assistant: assistant,
      options: {
        applyDefaults: applyDefaults,
      },
    });
  }

  public updateConnections(
    assistant: AssistantInterface,
    connections: AiConnection[],
    connectionStates: Array<{ connectionId: string; enabled: boolean }>,
  ): Observable<HttpResponse<null>> {
    return this.assistantApiService.setAssistantConnections({
      assistantKey: {
        id: assistant?.id,
        namespace: assistant?.namespace,
      },
      associationStates: connections
        ?.map((aiConnection) =>
          !aiConnection?.connection.isConnectionLocked
            ? {
                connectionKey: {
                  id: aiConnection.connection.id,
                  namespace: aiConnection.connection.namespace,
                  connectionType: aiConnection.connection.connectionType,
                },
                isAssociated:
                  connectionStates.find((state) => state.connectionId === aiConnection.connection.id)?.enabled ?? false,
              }
            : null,
        )
        .filter((connection) => connection !== null),
    });
  }

  private getConnectionName(
    connection: ConnectionInterface,
    widgetConnectionNames: Record<string, string>,
    phoneNumber: string | undefined,
    isChangeChannelsToTriggersEnabled: boolean,
  ): string {
    let connectionName = connection.name || '';
    if (
      (connection.connectionType === ConnectionType.WebchatWidget ||
        connection.connectionType === ConnectionType.SnapshotWidget) &&
      connection.id
    ) {
      connectionName = widgetConnectionNames[connection.id] || connection.name || '';
    }

    if (COMMUNICATION_CONNECTIONS.includes(connection.connectionType as ConnectionType)) {
      if (connection.connectionType === ConnectionType.Voice) {
        connectionName = this.translateService.instant('AI_ASSISTANT.SETTINGS.CONNECTIONS.VOICE_ACTION_LABEL');
      } else if (!isChangeChannelsToTriggersEnabled) {
        connectionName = this.translateService.instant('AI_ASSISTANT.SETTINGS.CONNECTIONS.RESPOND_TO', {
          connectionName,
        });
      } else if (
        connection.connectionType === ConnectionType.WebchatWidget ||
        connection.connectionType === ConnectionType.SnapshotWidget
      ) {
        connectionName = this.translateService.instant('AI_ASSISTANT.SETTINGS.CONNECTIONS.MESSAGE_RECEIVED_WEBCHAT', {
          connectionName,
        });
      } else if (connection.connectionType === ConnectionType.AutoReviewResponse) {
        connectionName = this.translateService.instant('AI_ASSISTANT.SETTINGS.CONNECTIONS.AUTO_REVIEW_RESPONSE', {
          connectionName,
        });
      } else {
        connectionName = this.translateService.instant('AI_ASSISTANT.SETTINGS.CONNECTIONS.MESSAGE_RECEIVED', {
          connectionName,
        });
      }
    }
    if (
      (connection.connectionType === ConnectionType.SMS || connection.connectionType === ConnectionType.Voice) &&
      phoneNumber
    ) {
      return `${connectionName} ${formatPhoneNumber(phoneNumber)}`;
    }
    return connectionName;
  }

  async updateKnowledgeWithChanges(knowledgeFormArray: AiKnowledgeFormArray): Promise<void> {
    const updatePromises: Promise<void | KnowledgeSource>[] = [];

    for (const knowledgeForm of knowledgeFormArray.controls) {
      if (knowledgeForm.dirty) {
        const newKnowledge = knowledgeForm.toKnowledgeSource();
        if (newKnowledge) {
          updatePromises.push(this.knowledgeService.updateKnowledgeSourceNameAndConfig(newKnowledge));
        }
      }
    }

    await Promise.all(updatePromises);
  }

  buildAiKnowledgeAppConfigurationUrl(assistantId: string): Observable<string> {
    return this.accountGroupId$.pipe(
      map((accountGroupId) => {
        if (accountGroupId) {
          return `/account/location/${accountGroupId}/ai/assistants/${assistantId}/edit`;
        }
        return `/ai/assistants/${assistantId}/edit`;
      }),
    );
  }

  async updateAssistantKnowledge(appId: string, appName: string, knowledgeForms: AiKnowledgeFormArray): Promise<void> {
    await this.updateKnowledgeWithChanges(knowledgeForms);
    const knowledgeSources = knowledgeForms.toKnowledgeSources();
    const configurationUrl = await firstValueFrom(
      this.assistantId$.pipe(
        switchMap(
          async (assistantId) =>
            await firstValueFrom(this.aiAssistantService.buildAiKnowledgeAppConfigurationUrl(assistantId)),
        ),
      ),
    );
    return this.knowledgeService.upsertKnowledgeForApp(
      appId,
      appName,
      configurationUrl,
      knowledgeAppType,
      knowledgeSources,
    );
  }
}
